// BUSINESS-49 (keputusan Owner 26 Sep 2026): koreksi JUMLAH serah terima yang salah dicatat (salah ketik,
// bukan barang kurang). Admin Produksi mengajukan jumlah benar + alasan; Kepala Cabang region (atau Owner)
// menyetujui/menolak; pemohon tidak memutus pengajuannya sendiri. Disetujui → catatan lama DIBATALKAN (tetap
// tampil di riwayat, dicoret) dan diganti catatan baru bertanda "hasil koreksi"; upah borongan dihitung ulang
// sistem dan tetap dibatasi jumlah di FA. Seluruh pagar ada di backend — layar ini hanya pintu.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/inventoryApi";
import { formatDateTime } from "@/lib/format";

const STATUS_LABEL = { DIAJUKAN: "Menunggu", DISETUJUI: "Disetujui", DITOLAK: "Ditolak" };

export function FormAjukanKoreksi({ handover, label, onSelesai, onBatal }) {
  const jahit = handover.from_stage_code === "JAHIT";
  // T74: catatan yang mencatat atasan & celana terpisah dikoreksi terpisah juga (Jahit: dari potong per baris).
  const berincian = !jahit && handover.qty_atasan !== null && handover.qty_atasan !== undefined;
  const [qty, setQty] = useState("");
  const [qtyA, setQtyA] = useState("");
  const [qtyC, setQtyC] = useState("");
  const [alasan, setAlasan] = useState("");
  const [items, setItems] = useState([]);
  const [potong, setPotong] = useState({});
  const [sibuk, setSibuk] = useState(false);
  const [galat, setGalat] = useState("");

  useEffect(() => {
    if (!jahit) return;
    api.get(`/borongan/fa/${handover.fa_id}/upah`)
      .then(({ data }) => setItems(data.items || []))
      .catch((e) => setGalat(apiErrorMessage(e)));
  }, [jahit, handover.fa_id]);

  const daftarPotong = Object.entries(potong)
    .map(([fa_item_id, v]) => ({ fa_item_id, qty_atasan: Number(v.a) || 0, qty_celana: Number(v.c) || 0 }))
    .filter((p) => p.qty_atasan + p.qty_celana > 0);
  const totalPotong = daftarPotong.reduce((t, p) => t + p.qty_atasan + p.qty_celana, 0);
  const qtyBaru = jahit ? totalPotong : berincian ? (Number(qtyA) || 0) + (Number(qtyC) || 0) : Number(qty) || 0;

  const kirim = async () => {
    setSibuk(true);
    setGalat("");
    try {
      await api.post("/borongan/koreksi-serah-terima", {
        handover_id: handover.id, qty_baru: qtyBaru, alasan: alasan.trim(),
        potong: jahit ? daftarPotong : [],
        ...(berincian ? { qty_atasan_baru: Number(qtyA) || 0, qty_celana_baru: Number(qtyC) || 0 } : {}),
      });
      toast.success("Koreksi diajukan — menunggu persetujuan.");
      onSelesai?.();
    } catch (e) {
      setGalat(apiErrorMessage(e));
    } finally {
      setSibuk(false);
    }
  };

  return (
    <div className="mt-2 space-y-2 rounded-lg border border-border bg-muted/30 p-3" data-testid="form-koreksi-serah-terima">
      <p className="text-xs text-muted-foreground">
        Koreksi {label} — tercatat {berincian ? `${handover.qty_atasan} atasan · ${handover.qty_celana} celana`
          : handover.qty_received}. Hanya untuk salah catat; barang yang memang kurang
        dicatat sebagai serah terima tertunda lalu susulan.
      </p>
      {jahit ? (
        <div className="space-y-1">
          <p className="text-xs font-medium text-foreground">Jumlah potong benar per baris item</p>
          {items.map((it) => (
            <div key={it.id} className="flex flex-wrap items-center gap-2 text-xs">
              <span className="min-w-[10rem] flex-1 text-muted-foreground">{it.description || it.product_type}</span>
              {Number(it.qty_atasan) > 0 && (
                <input type="number" min="0" inputMode="numeric" placeholder="atasan"
                       aria-label={`Atasan ${it.description || ""}`}
                       className="w-24 rounded border border-input bg-background px-2 py-1"
                       value={potong[it.id]?.a ?? ""}
                       onChange={(e) => setPotong((p) => ({ ...p, [it.id]: { ...p[it.id], a: e.target.value } }))} />
              )}
              {Number(it.qty_celana) > 0 && (
                <input type="number" min="0" inputMode="numeric" placeholder="celana"
                       aria-label={`Celana ${it.description || ""}`}
                       className="w-24 rounded border border-input bg-background px-2 py-1"
                       value={potong[it.id]?.c ?? ""}
                       onChange={(e) => setPotong((p) => ({ ...p, [it.id]: { ...p[it.id], c: e.target.value } }))} />
              )}
            </div>
          ))}
          <p className="text-xs tabular-nums text-muted-foreground">Jumlah benar: {totalPotong}</p>
        </div>
      ) : berincian ? (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="koreksi-per-jenis">
          <span className="font-medium text-foreground">Jumlah benar</span>
          <input type="number" min="0" inputMode="numeric" placeholder="atasan" aria-label="Atasan benar"
                 value={qtyA} onChange={(e) => setQtyA(e.target.value)}
                 className="w-24 rounded border border-input bg-background px-2 py-1" />
          <input type="number" min="0" inputMode="numeric" placeholder="celana" aria-label="Celana benar"
                 value={qtyC} onChange={(e) => setQtyC(e.target.value)}
                 className="w-24 rounded border border-input bg-background px-2 py-1" />
          <span className="tabular-nums text-muted-foreground">total {qtyBaru}</span>
        </div>
      ) : (
        <label className="block text-xs">
          <span className="font-medium text-foreground">Jumlah benar</span>
          <input type="number" min="1" inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value)}
                 className="mt-1 block w-32 rounded border border-input bg-background px-2 py-1" />
        </label>
      )}
      <label className="block text-xs">
        <span className="font-medium text-foreground">Alasan (minimal 10 huruf)</span>
        <textarea value={alasan} onChange={(e) => setAlasan(e.target.value)} rows={2}
                  className="mt-1 block w-full rounded border border-input bg-background px-2 py-1" />
      </label>
      {galat && <p className="text-xs text-destructive">{galat}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onBatal}
                className="rounded border border-border px-3 py-1 text-xs hover:bg-muted">Batal</button>
        <button type="button" onClick={kirim}
                disabled={sibuk || qtyBaru <= 0 || alasan.trim().length < 10}
                className="rounded bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground disabled:opacity-50">
          {sibuk ? "Mengirim…" : "Ajukan koreksi"}
        </button>
      </div>
    </div>
  );
}

export function DaftarKoreksiSerahTerima({ label, versi, onBerubah }) {
  const [items, setItems] = useState([]);
  const [galat, setGalat] = useState("");
  const [tolakId, setTolakId] = useState(null);
  const [alasanTolak, setAlasanTolak] = useState("");
  const [sibuk, setSibuk] = useState(false);

  const muat = useCallback(async () => {
    try {
      const { data } = await api.get("/borongan/koreksi-serah-terima");
      setItems(data.items || []);
      setGalat("");
    } catch (e) {
      setGalat(apiErrorMessage(e));
    }
  }, []);
  useEffect(() => { muat(); }, [muat, versi]);

  const putus = async (k, setuju) => {
    setSibuk(true);
    try {
      await api.post(`/borongan/koreksi-serah-terima/${k.id}/putuskan`, {
        setuju, alasan: setuju ? null : alasanTolak.trim(),
      });
      toast.success(setuju
        ? "Koreksi disetujui — catatan lama dibatalkan. Selisih upahnya masuk periode upah yang sedang berjalan."
        : "Koreksi ditolak.");
      setTolakId(null); setAlasanTolak("");
      await muat();
      onBerubah?.();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSibuk(false);
    }
  };

  const menunggu = items.filter((k) => k.status === "DIAJUKAN");
  const selesai = items.filter((k) => k.status !== "DIAJUKAN").slice(0, 10);
  if (!galat && items.length === 0) return null;

  const Baris = ({ k }) => (
    <div className="rounded-lg border border-border bg-card p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-foreground">{label(k.from_stage_code)}</span>
        <span className="text-muted-foreground">{k.fa_number}</span>
        <span className="tabular-nums">{k.qty_lama} → <b>{k.qty_baru}</b></span>
        <span className="rounded border border-border px-1.5 text-xs text-muted-foreground">
          {STATUS_LABEL[k.status] || k.status}
        </span>
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {k.alasan} — <span title={k.pemohon_email || undefined}>{k.pemohon_nama || "Admin Produksi"}</span>, {formatDateTime(k.created_at)}
        {k.diputus_pada && <> · diputus <span title={k.pemutus_email || undefined}>{k.pemutus_nama || ""}</span> {formatDateTime(k.diputus_pada)}</>}
        {k.alasan_putus && ` · ${k.alasan_putus}`}
      </p>
      {k.hanya_owner && (
        <p className="mt-1 text-xs text-muted-foreground" data-testid="koreksi-hanya-owner">
          WO ini sudah selesai lebih dari 7 hari kerja saat koreksinya diajukan. Persetujuannya hanya oleh Owner;
          Kepala Cabang tetap bisa menolak.
        </p>
      )}
      {/* K203 / VONIS-27SEP-BUSINESS-56-65 (BUSINESS-60 b): lewat batas, tombol Setujui hanya untuk Owner; Tolak tetap
          untuk Kepala Cabang region. `boleh_setuju`/`boleh_tolak` dihitung backend. */}
      {/* T38: koreksi yang pasti ditolak saat disetujui diberi tanda SEBELUM tombol diklik. */}
      {k.peringatan_tahap_sesudah && (
        <p className="mt-1 rounded border border-warning-soft-border bg-warning-soft px-2 py-1 text-xs text-warning-soft-foreground"
           data-testid={`koreksi-peringatan-tahap-${k.id}`}>
          {k.peringatan_tahap_sesudah}
        </p>
      )}
      {/* T37: tombol tidak tampil karena aturan dua-orang → sebutkan sebabnya, jangan diam. */}
      {k.diajukan_sendiri && (
        <p className="mt-1 text-xs text-muted-foreground" data-testid={`koreksi-diajukan-sendiri-${k.id}`}>
          Koreksi ini Anda ajukan sendiri — keputusannya oleh Kepala Cabang lain atau Owner.
        </p>
      )}
      {(k.boleh_setuju || k.boleh_tolak) && (
        tolakId === k.id ? (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input value={alasanTolak} onChange={(e) => setAlasanTolak(e.target.value)} placeholder="Alasan menolak"
                   className="min-w-[12rem] flex-1 rounded border border-input bg-background px-2 py-1 text-xs" />
            <button type="button" disabled={sibuk || alasanTolak.trim().length < 5} onClick={() => putus(k, false)}
                    className="rounded border border-destructive px-3 py-1 text-xs text-destructive disabled:opacity-50">
              Tolak
            </button>
            <button type="button" onClick={() => setTolakId(null)}
                    className="rounded border border-border px-3 py-1 text-xs">Batal</button>
          </div>
        ) : (
          <div className="mt-2 flex gap-2">
            {k.boleh_setuju && (
              <button type="button" disabled={sibuk} onClick={() => putus(k, true)} data-testid={`koreksi-st-setuju-${k.id}`}
                      className="rounded bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground disabled:opacity-50">
                Setujui
              </button>
            )}
            {k.boleh_tolak && (
              <button type="button" disabled={sibuk} onClick={() => setTolakId(k.id)} data-testid={`koreksi-st-tolak-${k.id}`}
                      className="rounded border border-border px-3 py-1 text-xs">Tolak…</button>
            )}
          </div>
        )
      )}
    </div>
  );

  return (
    <div data-testid="daftar-koreksi-serah-terima">
      <h2 className="mb-2 text-lg font-semibold text-foreground">Koreksi jumlah serah terima</h2>
      {galat && <p className="text-sm text-destructive">{galat}</p>}
      {menunggu.length > 0 && (
        <div className="mb-3 space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Menunggu keputusan</p>
          {menunggu.map((k) => <Baris key={k.id} k={k} />)}
        </div>
      )}
      {selesai.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Sudah diputus</p>
          {selesai.map((k) => <Baris key={k.id} k={k} />)}
        </div>
      )}
    </div>
  );
}
